import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { updateBrand } from "@/api/tenant";
import { useMenuLanguages } from "@/hooks/useMenuLanguages";
import { notify } from "@/lib/notify";

// Arama yokken gösterilen kapalı dil sayısı. Katalog 30+ ve hepsini pasif rozet olarak basmak kartı
// bir duvara çeviriyordu; bu kadarı "burada başka diller de var" demeye yetiyor.
const PREVIEW_COUNT = 8;

const LanguageBadge = ({ item, onClick }) => (
  <Badge
    size="lg"
    radius="sm"
    component="button"
    type="button"
    variant="outline"
    color="gray"
    style={{ cursor: "pointer", textTransform: "none" }}
    onClick={onClick}
    title={item.nativeLabel}
  >
    {item.flag} {item.label}
  </Badge>
);

const SortableLanguageBadge = ({ item, locked, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.code });

  return (
    <Badge
      ref={setNodeRef}
      size="lg"
      radius="sm"
      component="button"
      type="button"
      variant="filled"
      color="chocolate"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? "grabbing" : "grab",
        textTransform: "none",
        touchAction: "none",
      }}
      onClick={locked ? undefined : onClick}
      title={
        locked
          ? "Taban dil — kapatılamaz, sürükleyerek sıralayabilirsin"
          : `${item.nativeLabel} — kapatmak için tıkla, sıralamak için sürükle`
      }
      {...attributes}
      {...listeners}
    >
      {item.flag} {item.label}
    </Badge>
  );
};

// Menüde hangi dillerin açık olacağı.
//
// Katalog platformdan geliyor (Language tablosu, 30+ dil) — kiracı Fransızca isterse kimsenin kod
// yazması gerekmiyor, buradan işaretliyor ve ürün formlarında Fransızca alanları beliriyor.
//
// Taban dil KAPATILAMAZ: içeriğin `name`/`description` kolonlarında düz duran hâli o, menü onsuz
// hiçbir şey basamaz. Rozeti pasif çiziliyor, seçimden çıkarılamıyor.
export function MenuLanguages() {
  const queryClient = useQueryClient();
  const { loading, catalog, selectedCodes, baseCode } = useMenuLanguages();
  const [codes, setCodes] = useState([]);
  const [search, setSearch] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Sunucudan gelen seçim yerel duruma bir kez kopyalanıyor: kullanıcı işaretlerken her tıklama
  // istek atmıyor, "Kaydet"e basınca tek istek gidiyor.
  useEffect(() => {
    setCodes(selectedCodes);
  }, [selectedCodes]);

  const mutation = useMutation({
    mutationFn: (languages) => updateBrand({ languages }),
    onSuccess: () => {
      notify.success("Menü dilleri güncellendi");
      queryClient.invalidateQueries({ queryKey: ["menu-languages"] });
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Diller güncellenemedi"),
  });

  const toggle = (code) => {
    if (code === baseCode) {
      return;
    }

    setCodes((current) =>
      current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code],
    );
  };

  const isOn = (code) => code === baseCode || codes.includes(code);
  // Açık dillerin sırası KİRACININ: dizideki konum menüdeki dil seçicinin sırası (API tarafında
  // visibleLanguages aynı diziyi izliyor). Katalog `rank` yalnızca aşağıdaki kapalı dil vitrinini
  // sıralıyor.
  const byCode = new Map(catalog.map((item) => [item.code, item]));
  const selectedOrder =
    baseCode && !codes.includes(baseCode) ? [baseCode, ...codes] : codes;
  const on = selectedOrder.map((code) => byCode.get(code)).filter(Boolean);
  const onCodes = on.map((item) => item.code);
  const dirty = codes.join(",") !== selectedCodes.join(",");

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    setCodes(
      arrayMove(onCodes, onCodes.indexOf(active.id), onCodes.indexOf(over.id)),
    );
  };

  // Türkçe küçültme: 'İspanyolca' araması 'is' yazınca tutmalı. Düz toLowerCase 'İ' harfini
  // 'i̇' (noktalı i + birleşik nokta) yapıyor ve eşleşme kaçıyor.
  const fold = (value) => (value ?? "").toLocaleLowerCase("tr");
  const query = fold(search.trim());
  const matches = (item) =>
    !query ||
    fold(item.label).includes(query) ||
    fold(item.nativeLabel).includes(query) ||
    item.code === query;

  const off = catalog.filter((item) => !isOn(item.code) && matches(item));
  // Arama yokken tüm katalog 30+ pasif rozet demek — kart bir duvara dönüyordu. Kısa bir vitrin
  // gösterilip gerisi aramaya bırakılıyor; kullanıcı ne olduğunu görüyor ama ekran dolmuyor.
  const preview = query ? off : off.slice(0, PREVIEW_COUNT);
  const hidden = query ? 0 : off.length - preview.length;

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Text fw={600}>Menü Dilleri</Text>
            <Text size="xs" c="dimmed">
              Seçtiğin diller menüdeki dil seçicide çıkar ve ürün formlarında
              çeviri alanı açar
            </Text>
          </div>
          <Button
            size="compact-sm"
            disabled={!dirty}
            loading={mutation.isPending}
            onClick={() => mutation.mutate(codes)}
          >
            Kaydet
          </Button>
        </Group>

        {loading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <Stack gap="md">
            {/* Açık diller HER ZAMAN görünür, aramadan bağımsız: arama kutusu onları da
                            elese kullanıcı neyin açık olduğunu göremeden ekleme yapardı. */}
            <div>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb={6}>
                Açık ({on.length}) — sürükleyerek sırala
              </Text>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={onCodes} strategy={rectSortingStrategy}>
                  <Group gap="xs">
                    {on.map((item) => (
                      <SortableLanguageBadge
                        key={item.code}
                        item={item}
                        locked={item.code === baseCode}
                        onClick={() => toggle(item.code)}
                      />
                    ))}
                  </Group>
                </SortableContext>
              </DndContext>
              <Text size="xs" c="dimmed" mt={6}>
                Menüdeki dil seçici bu sırayı izler.
              </Text>
            </div>

            <div>
              <TextInput
                size="xs"
                placeholder={`${catalog.length} dil arasında ara — Fransızca, Français, fr`}
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                mb="xs"
              />
              <Group gap="xs">
                {preview.map((item) => (
                  <LanguageBadge
                    key={item.code}
                    item={item}
                    onClick={() => toggle(item.code)}
                  />
                ))}
              </Group>
              {preview.length === 0 && (
                <Text size="xs" c="dimmed">
                  Eşleşen dil yok.
                </Text>
              )}
              {hidden > 0 && (
                <Text size="xs" c="dimmed" mt={6}>
                  +{hidden} dil daha — aramak için yaz.
                </Text>
              )}
            </div>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
